import React from 'react';
import MetaTags from '~/components/MetaTags/MetaTags';
import Layout from '~/components/Layout/Layout';
import EventCard from '~/components/EventCard/EventCard';
import LoadingSpinner from '~/components/LoadingSpinner/LoadingSpinner';
import EventsDropdown from '~/components/EventsDropdown/EventsDropdown';
import TodaysAlertsTooltip from '~/components/TodaysAlertsTooltip/TodaysAlertsTooltip';
import StyledComponent from '~/components/Styled/Styled';
import normalizeDropdownItems from '~/util/normalizeDropdownItems';
import getOr from '~/util/getOr';
import isToday from '~/util/isToday';
import moment from 'moment';
import uniq from 'lodash/uniq';
import last from 'lodash/last';
import get from 'lodash/get';
import find from 'lodash/find';

import css from './Events.scss';

const formatEventDate = event =>
  event && event.game_date && moment(event.game_date).format('ddd, MMM D');

type Props = {
  pageData: any;
  loadingUpdate: boolean;
  NFLByeWeek: any;
  conferences: any;
  dropdownActiveItem: any;
  showEventsDropdown: boolean;
  onConferenceChange: any;
};

type State = {
  openTooltip: boolean;
};

class Events extends React.Component<Props, State> {
  static displayName = 'Events';

  static orderEventsByDate(events) {
    return events.sort((a, b) => new Date(a.game_date).valueOf() - new Date(b.game_date).valueOf());
  }

  static orderEvents(events) {
    return {
      currentEvents: events.filter(event => event.status === 'in_progress'),
      preEvents: events.filter(
        event =>
          event.status !== 'final' && event.status !== 'in_progress' && event.status !== 'postponed'
      ),
      postponedEvents: events.filter(event => event.status === 'postponed'),
      finalEvents: events.filter(event => event.status === 'final'),
    };
  }

  static renderCards(events) {
    const sortedEvents = Events.orderEventsByDate(events);

    return sortedEvents.map(event => (
      <div className={css.card} key={event.id}>
        <EventCard event={event} />
      </div>
    ));
  }

  static renderNFLByeWeek(events) {
    if (events.length === 0) {
      return <span className={css.nflByeWeekNoTeams}>No Teams</span>;
    }

    return events.map(event => {
      const { abbreviation, logos } = event;

      return (
        <div key={abbreviation} className={css.nflByeWeekTeam}>
          <img src={logos.tiny} alt={event.full_name} />
          <span>{abbreviation}</span>
        </div>
      );
    });
  }

  constructor(props) {
    super(props);
    this.state = {
      openTooltip: false,
    };
  }

  getDateRange() {
    const { schedule } = this.props.pageData;

    const currentEvent = getOr(schedule, 'current_group', last(schedule.current_season));

    if (currentEvent) {
      const end = moment(currentEvent.end_date);
      const start = moment(currentEvent.start_date);
      const range = end.diff(start, 'days');

      return range > 1 ? 'week' : 'day';
    }

    return 'day';
  }

  getSelectedDateLabel() {
    const { selectedDate, schedule } = this.props.pageData;
    const selectedDateObject = find(schedule.current_season, {
      id: selectedDate,
    });

    return get(selectedDateObject, 'label', '');
  }

  handleNoData(isLoading, events) {
    const { schedule } = this.props.pageData;

    return !isLoading && !schedule.current_group && events.length === 0;
  }

  renderEvents() {
    const { loadingUpdate } = this.props;
    const {
      events,
      currentLeague,
      selectedDate,
      schedule: { current_season },
    } = this.props.pageData;

    let nflSelectedWeek = null;

    if (currentLeague.slug === 'nfl') {
      nflSelectedWeek = current_season.find(season => season.id === selectedDate);
      nflSelectedWeek = getOr(nflSelectedWeek, 'season_type', '');
    }

    const dateRange = this.getDateRange();

    if (dateRange === 'week') {
      const days = uniq(events.map(formatEventDate));

      const totalEvents = {
        currentEvents: 0,
        preEvents: 0,
        postponedEvents: 0,
        finalEvents: 0,
      };

      let scheduleMap = days.map(day => {
        const dayEvents = Events.orderEvents(
          events.filter(event => formatEventDate(event) === day)
        );

        totalEvents.currentEvents += dayEvents.currentEvents.length;
        totalEvents.preEvents += dayEvents.preEvents.length;
        totalEvents.postponedEvents += dayEvents.postponedEvents.length;
        totalEvents.finalEvents += dayEvents.finalEvents.length;

        return {
          day,
          ...dayEvents,
        };
      });

      // Get the index from today's games, if available.
      const todayIndex = scheduleMap.findIndex(gameDay =>
        isToday(`${gameDay.day}, ${new Date().getFullYear()}`)
      );

      // Rearrange the Schedule to put today and the subsequent days first while putting the completed days at the end.
      if (todayIndex >= 0 && (totalEvents.currentEvents > 0 || totalEvents.preEvents > 0)) {
        scheduleMap = scheduleMap.slice(todayIndex).concat(scheduleMap.slice(0, todayIndex));
      }

      return (
        <div>
          {scheduleMap.map(day => (
            <div className={css.eventCardGridWeek}>
              <div className={css.date}>
                {day.day}
                {loadingUpdate &&
                (isToday(`${day.day}, ${new Date().getFullYear()}`) ||
                  day.currentEvents.length > 0) ? (
                  <div className={css.loader}>
                    <LoadingSpinner />
                  </div>
                ) : null}
              </div>
              <div className={css.eventCardGridWeekRow}>
                {Events.renderCards(day.currentEvents)}
                {Events.renderCards(day.preEvents)}
                {Events.renderCards(day.finalEvents)}
                {Events.renderCards(day.postponedEvents)}
              </div>
            </div>
          ))}
          {nflSelectedWeek === 'regular' ? <NFLByeWeek data={this.props.NFLByeWeek} /> : null}
        </div>
      );
    }

    const orderedEvents = Events.orderEvents(events);

    return (
      <div>
        <div className="row">
          <div className={`${css.date} col-xs-6 col-md-6`}>
            {formatEventDate(events[0])}
            {loadingUpdate ? (
              <div className={css.loader}>
                <LoadingSpinner />
              </div>
            ) : null}
          </div>
          <div className={`col-xs-6 col-md-6 ${css.getTodaysAlerts} ${css.mobileView}`}>
            <div
              className={css.openTooltip}
              onClick={() => this.setState({ openTooltip: true })}
              role="button"
              tabIndex={0}
              onBlur={e => {
                const { currentTarget } = e;
                setTimeout(() => {
                  if (!currentTarget.contains(document.activeElement)) {
                    this.setState({ openTooltip: false });
                  }
                }, 0);
              }}
            >
              Get Today&#39;s Alerts
              {this.state.openTooltip && (
                <div className={css.getTodaysAlertsTooltip}>
                  <TodaysAlertsTooltip
                    closeClick={() => setTimeout(() => this.setState({ openTooltip: false }), 0)}
                    leagueSlug={currentLeague.slug}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={css.eventCardGrid}>
          {Events.renderCards(orderedEvents.currentEvents)}
          {Events.renderCards(orderedEvents.preEvents)}
          {Events.renderCards(orderedEvents.finalEvents)}
          {Events.renderCards(orderedEvents.postponedEvents)}
        </div>
      </div>
    );
  }

  render() {
    const {
      conferences,
      dropdownActiveItem,
      showEventsDropdown,
      pageData: { isLoading, events },
      loadingUpdate,
    } = this.props;
    const currentLeague = this.props.pageData.currentLeague;
    const selectedDateLabel = this.getSelectedDateLabel();

    return (
      <Layout showEventsNav>
        <MetaTags content={currentLeague} type="events" />
        {showEventsDropdown ? (
          <EventsDropdown
            dropdownActiveItem={dropdownActiveItem}
            items={normalizeDropdownItems(conferences)}
            leagueSlug={this.props.pageData.currentLeague.slug}
            onClick={e => {
              this.props.onConferenceChange(e);
            }}
          />
        ) : null}
        <h1 className="sr-only">
          {get(currentLeague, 'medium_name', '')} Events for {selectedDateLabel}
        </h1>

        <section className={css.events}>
          {isLoading && !loadingUpdate ? (
            <div className={css.container}>
              <LoadingSpinner SpinnerType="circle_spin" />
            </div>
          ) : null}

          {(!isLoading || loadingUpdate) && events.length > 0 ? this.renderEvents() : null}

          {this.handleNoData(isLoading, events) ? <div>No data available</div> : null}
        </section>
      </Layout>
    );
  }
}

const NFLByeWeek = ({ data }: { data: object[] }) => (
  <div>
    <div className={css.date}>Bye Week</div>
    <div className={css.nflByeWeek}>{Events.renderNFLByeWeek(data)}</div>
  </div>
);

export default StyledComponent(Events, [css]);
